import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { DialPlanActionTypeValue } from '../../_types/dial-plan-action';
import { DialPlanActionNode } from '../../_services/dial-plans.service';
import { QueuesService } from '../../_services/queues.service';

interface ActionFormState {
  actionType: DialPlanActionTypeValue;
  sortOrder: number;
  queueId?: string;
  extensionNumber?: string;
  application?: string;
  data?: string;
  callerId?: string;
  audioFilename?: string;
  timeoutSeconds?: number;
  maxDigits?: number;
  waitSeconds?: number;
  dtmfDigit?: string;
  ttsText?: string;
  callerIdName?: string;
  callerNumber?: string;
  startTime?: string;
  endTime?: string;
  daysOfWeek?: string;
}

@Component({
    selector: 'app-dial-plan-action-edit',
    templateUrl: './dial-plan-action-edit.component.html',
    styleUrls: ['./dial-plan-action-edit.component.css'],
    standalone: false
})
export class DialPlanActionEditComponent implements OnInit {
  public form: ActionFormState = {
    actionType: 'CallQueue',
    sortOrder: 1
  };
  public queues: any[] = [];
  public isNew = true;

  // días marcados para CumpleHorario (ISO 1=Lunes … 7=Domingo); se serializa a CSV en form.daysOfWeek
  public selectedDays: number[] = [];
  public daysOptions = [
    { label: 'Lunes', value: 1 },
    { label: 'Martes', value: 2 },
    { label: 'Miércoles', value: 3 },
    { label: 'Jueves', value: 4 },
    { label: 'Viernes', value: 5 },
    { label: 'Sábado', value: 6 },
    { label: 'Domingo', value: 7 }
  ];

  public actionTypes = [
    { label: 'Cumple horario', value: 'CumpleHorario' },
    { label: 'El llamante es un cliente', value: 'CallerIsClient' },
    { label: 'El llamante es un operario', value: 'CallerIsWorker' },
    { label: 'El llamante es un perito', value: 'CallerIsExpert' },
    { label: 'El llamante es una compañía', value: 'CallerIsCompany' },
    { label: 'Enviar pulsación (DTMF)', value: 'SendDtmf' },
    { label: 'Es festivo', value: 'IsHoliday' },
    { label: 'Es horario de guardia', value: 'IsOnCallHours' },
    { label: 'Es horario normal', value: 'IsBusinessHours' },
    { label: 'Esperar', value: 'Wait' },
    { label: 'Esperar pulsación', value: 'WaitForInput' },
    { label: 'Establecer Caller ID', value: 'SetCallerId' },
    { label: 'Finalizar llamada', value: 'EndCall' },
    { label: 'Leer texto (TTS)', value: 'ReadText' },
    { label: 'Llamar a cola', value: 'CallQueue' },
    { label: 'Llamar a cola de contrato de expediente', value: 'CallCaseContractQueue' },
    { label: 'Llamar a extensión', value: 'CallExtension' },
    { label: 'Llamar a tramitador de expediente', value: 'CallCaseProcessor' },
    { label: 'Número que llama es', value: 'CallerNumberIs' },
    { label: 'Reproducir audio', value: 'PlayAudio' }
  ];

  constructor(
    private config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private messageService: MessageService,
    private queuesService: QueuesService
  ) { }

  async ngOnInit() {
    const existing: DialPlanActionNode | null = this.config.data?.action ?? null;
    const nextSortOrder: number = this.config.data?.nextSortOrder ?? 1;

    await this.loadQueues();

    if (existing) {
      this.isNew = false;
      this.form = {
        actionType: existing.actionType,
        sortOrder: existing.sortOrder,
        queueId: existing.queueId,
        extensionNumber: existing.extensionNumber,
        application: existing.application,
        data: existing.data,
        callerId: existing.callerId,
        audioFilename: existing.audioFilename,
        timeoutSeconds: existing.timeoutSeconds,
        maxDigits: existing.maxDigits,
        waitSeconds: existing.waitSeconds,
        dtmfDigit: existing.dtmfDigit,
        ttsText: existing.ttsText,
        callerIdName: existing.callerIdName,
        callerNumber: existing.callerNumber,
        startTime: existing.startTime,
        endTime: existing.endTime,
        daysOfWeek: existing.daysOfWeek
      };
      this.selectedDays = (existing.daysOfWeek ?? '')
        .split(',')
        .map(d => parseInt(d.trim(), 10))
        .filter(d => !isNaN(d));
    } else {
      this.isNew = true;
      this.form = {
        actionType: 'CallQueue',
        sortOrder: nextSortOrder
      };
    }
  }

  private async loadQueues() {
    const response = await firstValueFrom(this.queuesService.all({ page: { number: 1, size: 500 }, sort: ['name'] }));
    this.queues = response.data;
  }

  onTypeChange() {
    if (this.form.actionType !== 'CallQueue') this.form.queueId = undefined;
    if (this.form.actionType !== 'CallExtension') {
      this.form.extensionNumber = undefined;
      this.form.application = undefined;
      this.form.data = undefined;
      this.form.callerId = undefined;
    }
    if (this.form.actionType !== 'PlayAudio' && this.form.actionType !== 'WaitForInput') this.form.audioFilename = undefined;
    if (this.form.actionType !== 'WaitForInput') {
      this.form.timeoutSeconds = undefined;
      this.form.maxDigits = undefined;
    }
    if (this.form.actionType !== 'Wait') this.form.waitSeconds = undefined;
    if (this.form.actionType !== 'SendDtmf') this.form.dtmfDigit = undefined;
    if (this.form.actionType !== 'ReadText') this.form.ttsText = undefined;
    if (this.form.actionType !== 'SetCallerId') this.form.callerIdName = undefined;
    if (this.form.actionType !== 'CallerNumberIs') this.form.callerNumber = undefined;
    if (this.form.actionType !== 'CumpleHorario') {
      this.form.startTime = undefined;
      this.form.endTime = undefined;
      this.form.daysOfWeek = undefined;
      this.selectedDays = [];
    }
  }

  validate(): string | null {
    if (!this.form.actionType) return 'Seleccione un tipo';
    switch (this.form.actionType) {
      case 'CallQueue':
        if (!this.form.queueId) return 'Seleccione una cola';
        break;
      case 'CallExtension':
        if (!this.form.extensionNumber) return 'Indique la extensión';
        break;
      case 'PlayAudio':
        if (!this.form.audioFilename) return 'Indique el nombre del audio';
        break;
      case 'WaitForInput':
        if (!this.form.timeoutSeconds || this.form.timeoutSeconds <= 0) return 'Indique un timeout válido';
        if (!this.form.maxDigits || this.form.maxDigits <= 0) return 'Indique el número máximo de dígitos';
        break;
      case 'Wait':
        if (!this.form.waitSeconds || this.form.waitSeconds <= 0) return 'Indique los segundos de espera';
        break;
      case 'SendDtmf':
        if (!this.form.dtmfDigit) return 'Indique la pulsación a enviar';
        break;
      case 'ReadText':
        if (!this.form.ttsText?.trim()) return 'Indique el texto a leer';
        break;
      case 'SetCallerId':
        if (!this.form.callerIdName?.trim()) return 'Indique el nombre del llamante';
        break;
      case 'CallerNumberIs':
        if (!this.form.callerNumber?.trim()) return 'Indique el número a comprobar';
        break;
      case 'CumpleHorario':
        if (!this.form.startTime) return 'Indique la hora de inicio';
        if (!this.form.endTime) return 'Indique la hora de fin';
        if (this.selectedDays.length === 0) return 'Seleccione al menos un día';
        break;
    }
    return null;
  }

  save() {
    // serializamos los días marcados a CSV ISO antes de validar/devolver
    if (this.form.actionType === 'CumpleHorario') {
      this.form.daysOfWeek = [...this.selectedDays].sort((a, b) => a - b).join(',');
    }
    const err = this.validate();
    if (err) {
      this.messageService.add({ severity: 'warn', detail: err });
      return;
    }
    // devolvemos los datos al flow component; este los aplica al working tree
    this.dynamicDialogRef.close({ ...this.form });
  }

  cancel() {
    this.dynamicDialogRef.close(undefined);
  }
}
